import React, { useState, useEffect, useRef } from "react";
import { motion } from "motion/react";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Share2,
  BookOpen,
  Search,
  Heart,
  MessageCircle,
  Eye,
  Tag,
  Copy,
  ExternalLink,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Input } from "./ui/input";
import type {
  BlogPost,
  TOCItem,
  ReadingProgress,
} from "../types/blog";

interface BlogTemplateProps {
  post: BlogPost;
  relatedPosts?: BlogPost[];
  onLike?: (postId: string) => void;
  onShare?: (postId: string, platform: string) => void;
}

export function BlogTemplate({
  post,
  relatedPosts = [],
  onLike,
  onShare,
}: BlogTemplateProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState("");
  const [isLiked, setIsLiked] = useState(false);
  const [readingProgress, setReadingProgress] =
    useState<ReadingProgress>({
      progress: 0,
      currentSection: "",
      timeRemaining: "",
      wordsRead: 0,
    });
  const progressRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Extract headings from content for TOC
  const extractTOC = (content: string): TOCItem[] => {
    const headingRegex =
      /<h([2-6])(?:\s+id="([^"]*)")?[^>]*>(.*?)<\/h\1>/gi;
    const tocItems: TOCItem[] = [];
    let match;
    let index = 0;

    while ((match = headingRegex.exec(content)) !== null) {
      const level = parseInt(match[1]);
      const id = match[2] || `heading-${index}`;
      const title = match[3].replace(/<[^>]*>/g, "").trim();

      tocItems.push({
        id,
        title,
        level,
        href: `#${id}`,
      });
      index++;
    }

    return tocItems;
  };

  const tocItems = extractTOC(post.content);

  // Reading progress tracking
  useEffect(() => {
    const calculateReadingProgress = () => {
      if (!contentRef.current) return;

      const scrollY = window.scrollY || 0;
      const windowHeight = window.innerHeight || 0;
      const contentStart = contentRef.current?.offsetTop || 0;
      const contentHeight = contentRef.current?.scrollHeight || 0;

      // Calculate scroll progress
      const progress = Math.min(
        100,
        Math.max(
          0,
          ((scrollY + windowHeight - contentStart) /
            contentHeight) *
            100,
        ),
      );

      // Update progress bar
      if (progressRef.current) {
        progressRef.current.style.width = `${progress}%`;
      }

      // Estimate reading time remaining
      const wordsPerMinute = 200;
      const totalWords = post.content
        .replace(/<[^>]*>/g, "")
        .split(/\s+/).length;
      const wordsRead = Math.floor(
        (progress / 100) * totalWords,
      );
      const wordsRemaining = totalWords - wordsRead;
      const minutesRemaining = Math.ceil(
        wordsRemaining / wordsPerMinute,
      );

      setReadingProgress({
        progress,
        currentSection: activeSection,
        timeRemaining: `${minutesRemaining} min read`,
        wordsRead,
      });
    };

    const handleScroll = () => {
      calculateReadingProgress();
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    calculateReadingProgress(); // Initial calculation

    return () =>
      window.removeEventListener("scroll", handleScroll);
  }, [activeSection, post.content]);

  // TOC highlighting with Intersection Observer
  useEffect(() => {
    if (tocItems.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-20% 0px -70% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    // Observe all headings
    tocItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [tocItems]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (e.key) {
        case "/":
          e.preventDefault();
          document.getElementById("blog-search")?.focus();
          break;
        case "t":
        case "T":
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: "smooth" });
          break;
        case "l":
        case "L":
          e.preventDefault();
          handleLike();
          break;
        case "s":
        case "S":
          e.preventDefault();
          handleShare("copy");
          break;
        case "Escape":
          // Clear search
          setSearchQuery("");
          document.getElementById("blog-search")?.blur();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () =>
      window.removeEventListener("keydown", handleKeyDown);
  }, [isLiked]);

  // Search functionality
  const searchResults = React.useMemo(() => {
    if (!searchQuery.trim()) return null;

    const query = searchQuery.toLowerCase();
    const searchableContent = [
      { section: "Title", content: post.title, weight: 3 },
      { section: "Excerpt", content: post.excerpt, weight: 2 },
      {
        section: "Content",
        content: post.content.replace(/<[^>]*>/g, ""),
        weight: 1,
      },
      {
        section: "Tags",
        content: post.tags.join(" "),
        weight: 2,
      },
      {
        section: "Category",
        content: post.category,
        weight: 2,
      },
    ];

    return searchableContent
      .filter((item) =>
        item.content.toLowerCase().includes(query),
      )
      .map((item) => ({
        ...item,
        highlight: highlightText(item.content, query),
      }))
      .sort((a, b) => b.weight - a.weight);
  }, [searchQuery, post]);

  const highlightText = (text: string, query: string) => {
    const regex = new RegExp(`(${query})`, "gi");
    return text.replace(
      regex,
      '<mark class="bg-primary/20 text-primary">$1</mark>',
    );
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    if (onLike) {
      onLike(post.id);
    }
    toast.success(
      isLiked ? "Removed from favorites" : "Added to favorites",
      {
        icon: (
          <Heart
            className={`w-4 h-4 ${!isLiked ? "fill-current text-red-500" : ""}`}
          />
        ),
      },
    );
  };

  const handleShare = async (platform: string) => {
    const currentUrl = window.location.href;
    const shareData = {
      title: `${post.title} - amasQIS Blog`,
      text: post.excerpt,
      url: currentUrl,
    };

    try {
      switch (platform) {
        case "native":
          if (
            navigator.share &&
            navigator.canShare &&
            navigator.canShare(shareData)
          ) {
            await navigator.share(shareData);
            toast.success("Article shared successfully!");
          } else {
            handleShare("copy");
          }
          break;

        case "copy":
          await navigator.clipboard.writeText(currentUrl);
          toast.success("Article URL copied to clipboard!", {
            icon: <Copy className="w-4 h-4" />,
          });
          break;

        case "twitter":
          window.open(
            `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareData.title)}&url=${encodeURIComponent(currentUrl)}`,
            "_blank",
          );
          break;

        case "linkedin":
          window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
            "_blank",
          );
          break;

        case "facebook":
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
            "_blank",
          );
          break;

        case "email":
          window.open(
            `mailto:?subject=${encodeURIComponent(shareData.title)}&body=${encodeURIComponent(`${shareData.text}\n\n${currentUrl}`)}`,
            "_blank",
          );
          break;
      }

      if (onShare) {
        onShare(post.id, platform);
      }
    } catch (error) {
      toast.error("Unable to share article");
    }
  };

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatReadTime = (readingTime?: number) => {
    return readingTime ? `${readingTime} min` : "5 min";
  };

  return (
    <div className="pt-20 min-h-screen">
      {/* Reading Progress Bar */}
      <div
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-primary via-accent to-secondary z-[100] transition-all duration-300 ease-out"
        ref={progressRef}
        style={{ width: "0%" }}
      />

      {/* Sticky Header */}
      <motion.header
        className="sticky top-20 z-40 backdrop-blur-lg bg-background/80 border-b border-border/50"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/blog")}
                className="hover:bg-primary/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>

              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                <span className="text-sm text-muted-foreground font-medium">
                  amasQIS Blog
                </span>
                <Badge
                  variant="secondary"
                  className="bg-primary/20 text-primary"
                >
                  {post.category}
                </Badge>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="relative min-w-[280px] max-w-[420px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="blog-search"
                  type="search"
                  placeholder="Search article content... (press / to focus)"
                  value={searchQuery}
                  onChange={(e) =>
                    setSearchQuery(e.target.value)
                  }
                  className="pl-10 glass border-border/50 focus:border-primary"
                />
              </div>

              <Button
                onClick={() => handleShare("native")}
                variant="outline"
                className="border-primary/50 text-primary hover:bg-primary/10"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Layout */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Left Sidebar */}
          <motion.aside
            className="lg:col-span-1 order-2 lg:order-1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="sticky top-32 space-y-6">
              {/* Article Info */}
              <Card className="glass p-6">
                <h3 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wide">
                  Article Info
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">
                      {formatReadTime(post.readingTime)} read
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">
                      {formatDate(post.publishedDate)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">
                      {post.author.name}
                    </span>
                  </div>
                </div>

                {/* Reading Progress */}
                <Separator className="my-4" />
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      Reading Progress
                    </span>
                    <span className="text-primary font-medium">
                      {Math.round(readingProgress.progress)}%
                    </span>
                  </div>
                  <div className="w-full bg-muted/20 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-primary to-accent h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${readingProgress.progress}%`,
                      }}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {readingProgress.timeRemaining} remaining
                  </div>
                </div>

                {/* Stats */}
                <Separator className="my-4" />
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2">
                    <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                      <Eye className="w-3 h-3" />
                      <span>{post.views || 0}</span>
                    </div>
                  </div>
                  <div className="p-2">
                    <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                      <Heart className="w-3 h-3" />
                      <span>{post.likes || 0}</span>
                    </div>
                  </div>
                  <div className="p-2">
                    <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                      <MessageCircle className="w-3 h-3" />
                      <span>{post.comments || 0}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Table of Contents */}
              {tocItems.length > 0 && (
                <Card className="glass p-6">
                  <h3 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wide">
                    Contents
                  </h3>
                  <nav className="space-y-1">
                    {tocItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() =>
                          scrollToSection(item.href)
                        }
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                          activeSection === item.id
                            ? "bg-primary/20 text-primary border-l-2 border-primary"
                            : "text-muted-foreground hover:bg-muted/10 hover:text-foreground"
                        }`}
                        style={{
                          paddingLeft: `${(item.level - 2) * 12 + 12}px`,
                        }}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                </Card>
              )}

              {/* Quick Actions */}
              <Card className="glass p-6">
                <h3 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wide">
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  <Button
                    onClick={handleLike}
                    variant={isLiked ? "default" : "outline"}
                    size="sm"
                    className={`w-full ${
                      isLiked
                        ? "bg-red-500 text-white hover:bg-red-600"
                        : "border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950"
                    }`}
                  >
                    <Heart
                      className={`w-4 h-4 mr-2 ${isLiked ? "fill-current" : ""}`}
                    />
                    {isLiked ? "Liked!" : "Like Article"}
                  </Button>

                  {/* Share Dropdown */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => handleShare("twitter")}
                      variant="outline"
                      size="sm"
                      className="border-blue-500 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950"
                      title="Share on X/Twitter"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleShare("linkedin")}
                      variant="outline"
                      size="sm"
                      className="border-blue-700 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950"
                      title="Share on LinkedIn"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleShare("facebook")}
                      variant="outline"
                      size="sm"
                      className="border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
                      title="Share on Facebook"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleShare("copy")}
                      variant="outline"
                      size="sm"
                      className="border-primary/50 text-primary hover:bg-primary/10"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Keyboard Shortcuts */}
              <Card className="glass p-6">
                <h3 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wide">
                  Shortcuts
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <kbd className="px-2 py-1 bg-muted/20 rounded text-xs font-mono">
                      /
                    </kbd>
                    <span className="text-muted-foreground">
                      Focus search
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <kbd className="px-2 py-1 bg-muted/20 rounded text-xs font-mono">
                      T
                    </kbd>
                    <span className="text-muted-foreground">
                      Back to top
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <kbd className="px-2 py-1 bg-muted/20 rounded text-xs font-mono">
                      L
                    </kbd>
                    <span className="text-muted-foreground">
                      Like article
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <kbd className="px-2 py-1 bg-muted/20 rounded text-xs font-mono">
                      S
                    </kbd>
                    <span className="text-muted-foreground">
                      Share article
                    </span>
                  </div>
                </div>
              </Card>

              {/* Search Results */}
              {searchQuery &&
                searchResults &&
                searchResults.length > 0 && (
                  <Card className="glass p-6">
                    <h3 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wide">
                      Search Results ({searchResults.length})
                    </h3>
                    <div className="space-y-3">
                      {searchResults.map((result, index) => (
                        <div
                          key={index}
                          className="p-3 rounded-lg bg-primary/5 border border-primary/20"
                        >
                          <div className="text-xs font-medium text-primary mb-1">
                            {result.section}
                          </div>
                          <div
                            className="text-xs text-muted-foreground line-clamp-3"
                            dangerouslySetInnerHTML={{
                              __html: result.highlight,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

              {/* Tags */}
              <Card className="glass p-6">
                <h3 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wide">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="text-xs hover:bg-primary/10 transition-colors"
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              </Card>
            </div>
          </motion.aside>

          {/* Main Content */}
          <motion.main
            className="lg:col-span-3 order-1 lg:order-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="space-y-8">
              {/* Article Header */}
              <Card className="glass p-8">
                <div className="mb-6">
                  <Badge
                    variant="secondary"
                    className="bg-primary/20 text-primary mb-4"
                  >
                    {post.category}
                  </Badge>
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                    {post.title}
                  </h1>
                  <p className="text-xl text-muted-foreground mb-6 leading-relaxed">
                    {post.excerpt}
                  </p>

                  <div className="flex flex-wrap items-center gap-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="font-semibold">
                          {post.author.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {post.author.title || post.author.bio}
                        </div>
                      </div>
                    </div>

                    <Separator
                      orientation="vertical"
                      className="h-8"
                    />

                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.publishedDate)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {`${post.readingTime} min read`}
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {post.views || 0} views
                      </div>
                    </div>
                  </div>

                  {post.image && (
                    <div className="rounded-2xl overflow-hidden border border-border/50">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-64 md:h-96 object-cover"
                      />
                    </div>
                  )}
                </div>
              </Card>

              {/* Article Content */}
              <Card className="glass p-8">
                <div
                  ref={contentRef}
                  className="prose prose-lg max-w-none dark:prose-invert
                    prose-headings:text-foreground 
                    prose-p:text-muted-foreground 
                    prose-strong:text-foreground
                    prose-blockquote:border-l-primary
                    prose-blockquote:bg-primary/5
                    prose-blockquote:text-muted-foreground
                    prose-code:bg-primary/10
                    prose-code:text-primary
                    prose-pre:bg-muted/20
                    prose-pre:border
                    prose-pre:border-border/50
                    prose-a:text-primary
                    prose-a:no-underline
                    hover:prose-a:text-primary/80
                    prose-img:rounded-xl
                    prose-img:border
                    prose-img:border-border/50
                    prose-hr:border-border/50
                    prose-ul:text-muted-foreground
                    prose-ol:text-muted-foreground
                    prose-li:text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              </Card>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <Card className="glass p-8">
                  <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {relatedPosts.slice(0, 4).map((relatedPost) => (
                      <motion.div
                        key={relatedPost.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Link to={`/blog/${relatedPost.slug}`}>
                          <Card className="p-4 hover:border-primary/50 transition-all duration-300 group">
                            {relatedPost.image && (
                              <div className="relative h-32 rounded-lg overflow-hidden mb-3">
                                <img
                                  src={relatedPost.image}
                                  alt={relatedPost.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </div>
                            )}
                            <Badge
                              variant="outline"
                              className="border-primary/50 text-primary mb-2"
                            >
                              {relatedPost.category}
                            </Badge>
                            <h3 className="font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                              {relatedPost.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                              {relatedPost.excerpt}
                            </p>
                            <div className="flex items-center text-xs text-muted-foreground">
                              <div className="flex items-center gap-1 mr-3">
                                <Calendar className="w-3 h-3" />
                                {formatDate(relatedPost.publishedDate)}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {`${relatedPost.readingTime} min read`}
                              </div>
                            </div>
                          </Card>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Action Buttons */}
              <Card className="glass p-8">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <Button
                      onClick={handleLike}
                      variant={isLiked ? "default" : "outline"}
                      className={
                        isLiked
                          ? "bg-red-500 text-white hover:bg-red-600"
                          : "border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950"
                      }
                    >
                      <Heart
                        className={`w-4 h-4 mr-2 ${isLiked ? "fill-current" : ""}`}
                      />
                      {isLiked ? "Liked!" : "Like Article"}
                    </Button>
                    
                    <Button
                      onClick={() => handleShare("native")}
                      variant="outline"
                      className="border-primary/50 text-primary hover:bg-primary/10"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share Article
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => navigate("/blog")}
                      variant="ghost"
                      className="hover:bg-primary/10"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Blog
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </motion.main>
        </div>
      </div>
    </div>
  );
}